import { useEffect, useRef, useState } from 'react';
import { useAuth, ADMIN_ORIGINAL_TOKEN_KEY } from '@/lib/auth';
import { trackHeaderBar } from '@/lib/headerBars';

/**
 * Полоса «Вы вошли как <почта> — Вернуться в админку» на всех страницах,
 * пока админ смотрит сайт глазами пользователя («Войти как» в админке).
 *
 * Видна, только когда в браузере лежит сохранённый токен админа и сейчас
 * вошёл НЕ админ. Возврат — returnToAdmin (lib/auth.js): тот же токен админа,
 * что был до «Войти как», после проверки на сервере, без пароля.
 * Стоит самой верхней полосой: высота в --imp-h / --promo-bar-h
 * (lib/headerBars.js), промо-плашка и меню съезжают под неё.
 */
export default function ImpersonationBar() {
  const { user, isAuthenticated, loading, returnToAdmin } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  let hasAdminToken = false;
  try {
    hasAdminToken = !!localStorage.getItem(ADMIN_ORIGINAL_TOKEN_KEY);
  } catch (_e) {
    hasAdminToken = false;
  }
  const isAdminNow = !!(user && user.role === 'admin');
  const show = !loading && isAuthenticated && hasAdminToken && !isAdminNow;

  // Вошёл сам админ (вернулся паролем) — сохранённый токен больше не нужен.
  useEffect(() => {
    if (!loading && isAdminNow && hasAdminToken) {
      try { localStorage.removeItem(ADMIN_ORIGINAL_TOKEN_KEY); } catch (_e) { /* нет хранилища */ }
    }
  }, [loading, isAdminNow, hasAdminToken]);

  useEffect(() => trackHeaderBar('imp', ref.current), [show]);

  if (!show) return null;

  const back = async () => {
    setBusy(true);
    await returnToAdmin();
  };

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-[60] bg-slate-900 text-white"
      data-testid="impersonation-bar"
    >
      <div className="max-w-6xl mx-auto px-3 py-1 flex items-center justify-center gap-3 flex-wrap text-xs sm:text-sm leading-snug">
        <span className="min-w-0 break-all">
          Вы вошли как <b>{user && user.email}</b>
        </span>
        <button
          type="button"
          onClick={back}
          disabled={busy}
          className="font-semibold bg-white text-slate-900 rounded-md px-2.5 py-0.5 hover:bg-slate-100 disabled:opacity-60 whitespace-nowrap"
          data-testid="impersonation-return"
        >
          {busy ? 'Возвращаю…' : '← Вернуться в админку'}
        </button>
      </div>
    </div>
  );
}
